import { MakeupStatus } from "@/openapi/generated";

export type MakeupStatusLabelOptions = {
  changeRequestedLabel?: string;
};

export type MakeupStatusBadgeColors = {
  color: string;
  bgDark: string;
  bgLight: string;
  borderColor: string;
};

const BADGE_COLORS = {
  success: {
    color: "green.500",
    bgDark: "rgba(34,197,94,0.15)",
    bgLight: "rgba(34,197,94,0.12)",
    borderColor: "rgba(34,197,94,0.35)",
  },
  danger: {
    color: "red.500",
    bgDark: "rgba(239,68,68,0.15)",
    bgLight: "rgba(239,68,68,0.12)",
    borderColor: "rgba(239,68,68,0.35)",
  },
  info: {
    color: "blue.500",
    bgDark: "rgba(57,101,255,0.15)",
    bgLight: "rgba(57,101,255,0.12)",
    borderColor: "rgba(57,101,255,0.35)",
  },
  action: {
    color: "orange.400",
    bgDark: "rgba(249,115,22,0.15)",
    bgLight: "rgba(249,115,22,0.12)",
    borderColor: "rgba(249,115,22,0.35)",
  },
  neutral: {
    color: "gray.400",
    bgDark: "rgba(148,163,184,0.15)",
    bgLight: "rgba(148,163,184,0.12)",
    borderColor: "rgba(148,163,184,0.35)",
  },
} as const satisfies Record<string, MakeupStatusBadgeColors>;

export const MEMBER_CHANGE_REQUESTED_LABEL = "Needs your response";

export function makeupStatusLabel(
  status: MakeupStatus,
  options?: MakeupStatusLabelOptions,
): string {
  switch (status) {
    case MakeupStatus.Pending:
      return "Pending approval";
    case MakeupStatus.Approved:
      return "Approved";
    case MakeupStatus.ChangeRequested:
      return options?.changeRequestedLabel ?? "Change proposed";
    case MakeupStatus.Rejected:
      return "Rejected";
    case MakeupStatus.Cancelled:
      return "Cancelled";
    default:
      return status;
  }
}

export function makeupStatusBadgeColors(
  status: MakeupStatus,
): MakeupStatusBadgeColors {
  switch (status) {
    case MakeupStatus.Approved:
      return BADGE_COLORS.success;
    case MakeupStatus.Rejected:
    case MakeupStatus.Cancelled:
      return BADGE_COLORS.danger;
    case MakeupStatus.Pending:
      return BADGE_COLORS.info;
    case MakeupStatus.ChangeRequested:
      return BADGE_COLORS.action;
    default:
      return BADGE_COLORS.neutral;
  }
}

export function isMakeupStatusActive(status: MakeupStatus): boolean {
  return (
    status === MakeupStatus.Pending || status === MakeupStatus.ChangeRequested
  );
}

export function makeupStatusNeedsParentAction(status: MakeupStatus): boolean {
  return status === MakeupStatus.ChangeRequested;
}

export function countMakeupsNeedingParentAction(
  sessions: { status: MakeupStatus }[],
): number {
  return sessions.filter((session) =>
    makeupStatusNeedsParentAction(session.status),
  ).length;
}
